import useControlled from '@material-ui/core/utils/useControlled';
import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import React, { cloneElement, createElement, isValidElement } from 'react';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
import CollapseValidation from '../../Validation/CollapseValidation';
import { RadioButtonGroupContext } from '../useRadioButtonGroup';
const COMPONENT_NAME = 'RadioButtonGroup';
const RadioButtonGroup = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { id, className, children = [], container = 'div', validationText, hideValidationText, hideValidationElement, validationClassName, header, inline, onChange, value: valueProp, defaultValue, name = 'RadioButtonGroup' } = props;
    const [value, setValue] = useControlled({
        controlled: valueProp,
        default: defaultValue,
        name
    });
    const handleChange = (e, value)=>{
        setValue(value);
        onChange?.(e, value);
    };
    const headerElement = header ? /*#__PURE__*/ React.createElement("div", {
        className: "RadioButtonGroup-header"
    }, /*#__PURE__*/ React.createElement("h3", {
        className: "RadioButtonGroup-heading"
    }, header)) : null;
    const validationElement = hideValidationElement ? null : /*#__PURE__*/ React.createElement(CollapseValidation, {
        hideValidationText: hideValidationText,
        validationText: validationText,
        additionalClassName: cx('RadioButtonGroup-collapseValidation'),
        validationClassName: cx('RadioButtonGroup-validation', validationClassName)
    });
    const childrenWithItem = React.Children.map(children, (child)=>child ? /*#__PURE__*/ React.createElement("div", {
            key: child.key || (child.props.value ?? child.props.label),
            className: "RadioButtonGroup-item"
        }, child) : null);
    const containerProps = {
        id,
        className: cx('RadioButtonGroup', className, {
            'RadioButtonGroup--inline': inline
        })
    };
    const createFn = /*#__PURE__*/ isValidElement(container) ? cloneElement : createElement;
    return /*#__PURE__*/ React.createElement(RadioButtonGroupContext.Provider, {
        value: {
            name,
            onChange: handleChange,
            value
        }
    }, createFn(container, containerProps, headerElement, childrenWithItem, validationElement));
};
RadioButtonGroup.displayName = 'RadioButtonGroup';
RadioButtonGroup.propTypes = {
    /**
	 * Any number of `RadioButton` elements.
	 */ children: PropTypes.node,
    /**
	 * Root className.
	 */ className: PropTypes.string,
    /**
	 * Wrapper element such as `div` or `span` that wrap all child radio elements.
	 */ container: PropTypes.string,
    /**
	 * Determins which radio will be selected by default on first render.
	 */ defaultValue: PropTypes.any,
    /**
	 * Header is defined show header
	 */ header: PropTypes.string,
    /**
	 * Whether to render validation element.
	 */ hideValidationElement: PropTypes.bool,
    /**
	 * Whether to render validation component.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * id attribute of wrapping container
	 */ id: PropTypes.string,
    /**
	 * Radios will be displayed inline
	 */ inline: PropTypes.bool,
    /**
	 * Name of radio button group.
	 */ name: PropTypes.string,
    /**
	 * Callback function fired when any of radion buttons is selected.
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothin'
	 */ onChange: PropTypes.func,
    /**
	 * Set class for validation component.
	 */ validationClassName: PropTypes.string,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Determins selected radio value.
	 */ value: PropTypes.any
};
export { RadioButtonGroup };
export default RadioButtonGroup;
